// src/game.js
import { createPlayer } from "./player.js";
import { spawnEnemy } from "./enemy.js";
import { setupUI } from "./ui.js";

export function gameScene(k) {
  k.scene("game", () => {
    // --- STATE MANAGEMENT ---
    const state = {
      constants: {
        SPEED: 140,
        ARENA_PADDING: 12,
        PLAYER_MAX_HP: 6,
        PUNCH_COOLDOWN: 0.15,
        SPECIAL_COOLDOWN: 2.4,
        SPECIAL_DAMAGE: 3,
        SPECIAL_KNOCKBACK: 120,
        BASE_ENEMY_HP: 3,
        ENEMY_SPEED: 70,
        ENEMY_ATTACK_COOLDOWN: 1.6,
        ENEMY_PUNCH_RANGE: 34,
        SCORE_PER_ENEMY: 100,
        HUD_MARGIN: 20,
        HUD_HEIGHT: 150,
        HP_BAR_WIDTH: 200,
        HP_BAR_HEIGHT: 10,
        BASE_DAMAGE: 1,
        CHARGED_DAMAGE: 3,
        CHARGE_TIME: 0.55,
        CHARGED_COOLDOWN: 0.6,
        COMBO_RESET_TIME: 3,
        COMBO_BONUS: 0.15,
        BACKDROP_WIDTH: 2912,
        BACKDROP_HEIGHT: 1632,
        COOLDOWN_BAR_WIDTH: 132,
        POWERUP_SPAWN_CHANCE: 0.65,
        POWERUP_LIFESPAN: 8,
        POWERUP_HEALTH_BOOST: 3,
        POWERUP_DURATION: 10,
        POWERUP_DAMAGE_MULTIPLIER: 1.8,
        POWERUP_SPEED_MULTIPLIER: 1.5,
        ENEMY_SPEED_WAVE_SCALING: 1.02,
        ENEMY_ATTACK_COOLDOWN_WAVE_SCALING: 0.99,
        MIN_ENEMY_ATTACK_COOLDOWN: 0.7,
        ENEMIES_PER_WAVE_CAP: 4,
        ENEMIES_PER_WAVE_GROWTH: 0.4,
        WAVE_SPAWN_DELAY: 0.35,
      },
      menuActive: true,
      tutorialActive: false,
      gameOver: false,
      wave: 1,
      score: 0,
      highScore: 0,
      player: null,
      playerShadow: null,
      currentEnemy: null,
      enemies: [],
      enemiesRemaining: 0,
      playerFacing: k.vec2(1, 0),
      comboCount: 0,
      comboTimer: 0,
      isCharging: false,
      chargeStart: 0,
      chargeAura: null,
      chargeReady: false,
      lastDashSmokeTime: 0,
      bgMusicController: null,
      activePowerUp: null,
      powerUpTimer: 0,
    };

    state.highScore = parseInt(localStorage.getItem("highscore") || "0");
    state.tutorialActive = localStorage.getItem("seen_tutorial") !== "1";

    const ui = setupUI(k, state);
    ui.onStart = () => beginFight();
    ui.onReplay = () => restartGame();
    ui.onMenu = () => returnToMenu();
    ui.onTutorialContinue = () => exitTutorial();
    const { player, playerShadow } = createPlayer(k, state);
    state.player = player;
    state.playerShadow = playerShadow;

    const powerUpAura = k.add([k.circle(32), k.pos(player.pos), k.opacity(0), k.z(player.z - 1)]);

    // --- PARTICLE EFFECTS ---
    state.spawnSlashParticles = (origin, facing, tint = k.rgb(150, 220, 255)) => {
        const dir = facing.len() > 0 ? facing.unit() : k.vec2(1, 0);
        for (let i = 0; i < 6; i++) {
            const jitter = k.vec2((Math.random() - 0.5) * 12, (Math.random() - 0.5) * 8);
            const spark = k.add([
                k.pos(origin.add(jitter.scale(0.25))),
                k.rect(6, 2, { radius: 1 }),
                k.color(tint.r, tint.g, tint.b),
                k.opacity(1),
                k.lifespan(0.3, { fade: 0.15 }),
                { vel: dir.scale(220 + Math.random() * 140).add(jitter.scale(8)) },
            ]);
            spark.onUpdate(() => {
                spark.pos = spark.pos.add(spark.vel.scale(k.dt()));
                spark.vel = spark.vel.scale(0.88);
            });
        }
    };
    state.spawnImpactBurst = (origin, tint = k.rgb(255, 150, 110)) => {
        for (let i = 0; i < 8; i++) {
            const angle = Math.random() * Math.PI * 2;
            const speed = 120 + Math.random() * 160;
            const vel = k.vec2(Math.cos(angle), Math.sin(angle)).scale(speed);
            const particle = k.add([
                k.pos(origin),
                k.rect(4, 4, { radius: 2 }),
                k.color(tint.r, tint.g, tint.b),
                k.opacity(1),
                k.lifespan(0.35, { fade: 0.2 }),
                { vel },
            ]);
            particle.onUpdate(() => {
                particle.pos = particle.pos.add(particle.vel.scale(k.dt()));
                particle.vel = particle.vel.scale(0.9);
            });
        }
    };
    state.spawnDashSmoke = (origin, dir) => {
        if (state.menuActive || state.gameOver) return;
        const now = k.time();
        if (now - state.lastDashSmokeTime < 0.09) return;
        state.lastDashSmokeTime = now;
        const puff = k.add([
            k.pos(origin.add(dir.scale(-6))),
            k.rect(10, 6, { radius: 3 }),
            k.color(60, 80, 120),
            k.opacity(0.4),
            k.lifespan(0.35, { fade: 0.3 }),
            { vel: dir.scale(-60).add(k.vec2((Math.random() - 0.5) * 50, (Math.random() - 0.5) * 30)) },
        ]);
        puff.onUpdate(() => {
            puff.pos = puff.pos.add(puff.vel.scale(k.dt()));
            puff.vel = puff.vel.scale(0.9);
        });
    };
    state.spawnParrySparks = (origin) => {
        for (let i = 0; i < 12; i++) {
            const angle = (Math.PI * 2 * i) / 12 + Math.random() * 0.3;
            const vel = k.vec2(Math.cos(angle), Math.sin(angle)).scale(120 + Math.random() * 80);
            const spark = k.add([
                k.pos(origin),
                k.rect(3, 2, { radius: 1 }),
                k.color(255, 220, 150),
                k.opacity(1),
                k.lifespan(0.25, { fade: 0.12 }),
                { vel },
            ]);
            spark.onUpdate(() => {
                spark.pos = spark.pos.add(spark.vel.scale(k.dt()));
                spark.vel = spark.vel.scale(0.82);
            });
        }
    };
    const getFacingDirection = () => {
        const len = Math.hypot(state.playerFacing.x, state.playerFacing.y);
        if (len === 0) return k.vec2(1, 0);
        return k.vec2(state.playerFacing.x / len, state.playerFacing.y / len);
    };

    const getArenaBounds = () => {
        const { ARENA_PADDING, HUD_HEIGHT } = state.constants;
        const halfW = 16;
        const halfH = 16;
        return {
            minX: ARENA_PADDING + halfW,
            maxX: k.width() - ARENA_PADDING - halfW,
            minY: HUD_HEIGHT + 30 + halfH,
            maxY: k.height() - ARENA_PADDING - halfH,
        };
    };

    const getRandomSpawnPosition = (minDistance = 120) => {
        const bounds = getArenaBounds();
        let pos = k.vec2((bounds.minX + bounds.maxX) / 2, (bounds.minY + bounds.maxY) / 2);
        for (let i = 0; i < 6; i++) {
            const x = bounds.minX + Math.random() * (bounds.maxX - bounds.minX);
            const y = bounds.minY + Math.random() * (bounds.maxY - bounds.minY);
            pos = k.vec2(x, y);
            if (!state.player || !state.player.exists()) break;
            if (pos.dist(state.player.pos) >= minDistance) break;
        }
        return pos;
    };

    const getEnemiesPerWave = (wave) => {
        const { ENEMIES_PER_WAVE_CAP, ENEMIES_PER_WAVE_GROWTH } = state.constants;
        const count = 1 + Math.floor((wave - 1) * ENEMIES_PER_WAVE_GROWTH);
        return Math.min(ENEMIES_PER_WAVE_CAP, count);
    };

    const pickEnemyType = () => {
        if (state.wave >= 5) return k.choose(["brawler", "sprinter", "tank"]);
        if (state.wave >= 3) return k.choose(["brawler", "sprinter"]);
        return "brawler";
    };

    const spawnSingleEnemy = () => {
        if (state.menuActive || state.gameOver) return;
        const type = pickEnemyType();
        const foe = spawnEnemy(k, state, { type, spawnPos: getRandomSpawnPosition() });
        if (foe) {
            state.enemies.push(foe);
            state.currentEnemy = foe;
        }
    };

    const startWave = (wave) => {
        state.wave = wave;
        state.enemiesRemaining = getEnemiesPerWave(wave);
        for (let i = 0; i < state.enemiesRemaining; i++) {
            k.wait(i * state.constants.WAVE_SPAWN_DELAY, () => {
                if (!state.gameOver && !state.menuActive) spawnSingleEnemy();
            });
        }
        if (wave === 2) {
            k.wait(0.2, () => {
                if (!state.gameOver && !state.menuActive) {
                    spawnPowerUp(getRandomSpawnPosition(60));
                }
            });
        }
    };

    const updateCurrentEnemy = () => {
        if (!state.player || !state.player.exists()) {
            state.currentEnemy = null;
            return;
        }
        let closest = null;
        let closestDist = Infinity;
        state.enemies.forEach((foe) => {
            if (!foe || !foe.exists() || foe.isDefeated) return;
            const dist = foe.pos.dist(state.player.pos);
            if (dist < closestDist) {
                closest = foe;
                closestDist = dist;
            }
        });
        state.currentEnemy = closest;
    };
    
    // --- POWER-UPS ---
    function spawnPowerUp(pos) {
        const types = ["health", "damage", "speed"];
        const type = k.choose(types);
        
        const powerUp = k.add([
            k.sprite("bean"),
            k.pos(pos),
            k.area(),
            k.opacity(1),
            k.lifespan(state.constants.POWERUP_LIFESPAN, { fade: 0.5 }),
            "powerup",
            { type: type },
        ]);

        if (type === "damage") powerUp.color = k.rgb(255, 100, 100);
        else if (type === "speed") powerUp.color = k.rgb(100, 150, 255);
        else powerUp.color = k.rgb(100, 255, 100);

        powerUp.onCollide("player", () => {
            k.destroy(powerUp);
            switch (type) {
                case "health":
                    player.hp = Math.min(state.constants.PLAYER_MAX_HP, player.hp + state.constants.POWERUP_HEALTH_BOOST);
                    k.play("sfx_powerup_health");
                    break;
                case "damage":
                    state.activePowerUp = "damage";
                    state.powerUpTimer = state.constants.POWERUP_DURATION;
                    k.play("sfx_powerup_damage");
                    powerUpAura.color = k.rgb(255, 100, 100);
                    powerUpAura.opacity = 0.3;
                    break;
                case "speed":
                    state.activePowerUp = "speed";
                    state.powerUpTimer = state.constants.POWERUP_DURATION;
                    k.play("sfx_powerup_speed");
                    powerUpAura.color = k.rgb(100, 150, 255);
                    powerUpAura.opacity = 0.3;
                    break;
            }
        });
    }

    // --- CORE GAME LOGIC ---
    function beginFight() {
        if (!state.menuActive || state.tutorialActive) return;
        k.play("sfx_ui_select");
        state.menuActive = false;
        ui.hideMenu();
        player.hidden = false;
        playerShadow.hidden = false;
        resetPlayerState();
        player.play("idle");
        k.play("sfx_start", { volume: 0.7 });
        if (!state.bgMusicController) {
            state.bgMusicController = k.play("bg_theme", { volume: 0.55, loop: true });
        }
        cleanupChargeAura();
        state.enemies = [];
        startWave(1);
    }

    function exitTutorial() {
        state.tutorialActive = false;
        localStorage.setItem("seen_tutorial", "1");
        ui.hideTutorial();
        ui.showMenu();
    }

    function destroyAllEnemies() {
        state.enemies.forEach((foe) => {
            if (foe && foe.exists()) k.destroy(foe);
        });
        state.enemies = [];
        state.currentEnemy = null;
        state.enemiesRemaining = 0;
    }

    function returnToMenu() {
        k.play("sfx_ui_select");
        state.gameOver = false;
        state.menuActive = true;
        destroyAllEnemies();
        ui.hideGameOver();
        resetPlayerState();
        ui.showMenu();
    }
    
    function resetPlayerState() {
        const { PLAYER_MAX_HP, ARENA_PADDING, HUD_HEIGHT } = state.constants;
        player.hp = PLAYER_MAX_HP;
        player.canPunch = true;
        player.cooldownTimer = 0;
        player.specialReady = true;
        player.specialCooldownTimer = 0;
        const halfW = (player.width ?? 32) / 2;
        const halfH = (player.height ?? 32) / 2;
        const minX = ARENA_PADDING + halfW;
        const maxX = k.width() - ARENA_PADDING - halfW;
        const minY = HUD_HEIGHT + 30 + halfH;
        const maxY = k.height() - ARENA_PADDING - halfH;
        const spawnX = k.clamp(k.width() * 0.28, minX, maxX);
        const spawnY = k.clamp(k.height() * 0.65, minY, maxY);
        player.pos = k.vec2(spawnX, spawnY);
        playerShadow.pos = k.vec2(player.pos.x, player.pos.y + 20);
        player.hidden = state.menuActive;
        playerShadow.hidden = state.menuActive;
        state.playerFacing = k.vec2(1, 0);
        if (player.exists()) player.play("idle");
        resetCombo();
    }

    state.damagePlayer = (amount = 1) => {
        if (state.gameOver) return;
        player.hp = Math.max(0, player.hp - amount);
        k.shake(4);
        player.play("hurt");
        k.play("sfx_hurt", { volume: 0.6 });
        state.spawnParrySparks(player.pos);
        resetCombo();
        cleanupChargeAura();
        k.wait(0.35, () => {
            if (!state.gameOver && player.exists()) player.play("idle");
        });
        if (player.hp <= 0) {
            triggerGameOver();
        }
    };

    const handleEnemyDefeated = (foe) => {
        if (Math.random() < state.constants.POWERUP_SPAWN_CHANCE) {
            spawnPowerUp(foe.pos);
        }
        state.score += state.constants.SCORE_PER_ENEMY;
        state.enemiesRemaining = Math.max(0, state.enemiesRemaining - 1);
        const bonus = k.add([
            k.text(`+${state.constants.SCORE_PER_ENEMY}`, { size: 20, font: "font_arcade" }),
            k.pos(k.vec2(foe.pos)),
            k.anchor("center"),
            k.color(180, 255, 210),
            k.opacity(1),
            k.z(40),
            k.lifespan(0.6, { fade: 0.2 }),
        ]);
        bonus.onUpdate(() => bonus.pos = bonus.pos.add(k.vec2(0, -60 * k.dt())));
        if (state.enemiesRemaining <= 0) {
            k.wait(0.8, () => {
                if (!state.gameOver && !state.menuActive) {
                    startWave(state.wave + 1);
                }
            });
        }
    };

    function damageEnemy(foe, amount = 1) {
        if (!foe.exists() || foe.isDefeated) return;
        
        let finalAmount = amount;
        if (state.activePowerUp === "damage") {
            finalAmount *= state.constants.POWERUP_DAMAGE_MULTIPLIER;
        }
        const multiplier = getComboMultiplier();
        finalAmount = Math.max(1, Math.round(finalAmount * multiplier));
        
        foe.hp = Math.max(0, foe.hp - finalAmount);
        k.shake(2);
        if (!foe.isDefeated) {
            foe.play("hurt");
            k.wait(0.3, () => {
                if (foe.exists() && !foe.isDefeated) foe.play("idle");
            });
        }
        state.spawnImpactBurst(foe.pos, k.rgb(255, 170, 120));
        k.play("sfx_punch", { volume: 0.55 });

        if (foe.hp <= 0) {
            foe.isDefeated = true;
            handleEnemyDefeated(foe);
            k.destroy(foe);
        }
    }
    
    function triggerGameOver() {
        if (state.gameOver) return;
        state.gameOver = true;
        if (state.score > state.highScore) {
            state.highScore = state.score;
            localStorage.setItem("highscore", state.highScore);
        }
        destroyAllEnemies();
        ui.showGameOver();
        k.shake(6);
    }
    
    function restartGame() {
        k.play("sfx_ui_select");
        state.gameOver = false;
        state.wave = 1;
        state.score = 0;
        state.enemiesRemaining = 0;
        ui.hideGameOver();
        resetPlayerState();
        startWave(1);
    }
    
    // --- COMBO ---
    const getComboMultiplier = () => Math.min(1 + state.comboCount * state.constants.COMBO_BONUS, 3);
    const resetCombo = () => {
        if (state.comboCount > 3) k.play("sfx_break", { volume: 0.45 });
        state.comboCount = 0;
        state.comboTimer = 0;
    };
    const registerCombo = () => {
        state.comboCount++;
        state.comboTimer = state.constants.COMBO_RESET_TIME;
        if (state.comboCount > 1) k.play("sfx_combo", { volume: 0.4 + Math.min(0.4, state.comboCount * 0.05) });
    };

    // --- ATTACKS ---
    const cleanupChargeAura = () => {
        if (state.chargeAura && state.chargeAura.exists()) k.destroy(state.chargeAura);
        state.chargeAura = null;
        state.isCharging = false;
        state.chargeReady = false;
    };

    function performLightAttack() {
        if (!player.canPunch) return;
        const { PUNCH_COOLDOWN, BASE_DAMAGE } = state.constants;
        player.canPunch = false;
        player.cooldownTimer = PUNCH_COOLDOWN;
        player.play("punch");
        k.play("sfx_punch", { volume: 0.65 });
        k.wait(0.2, () => { if (!state.gameOver && player.exists()) player.play("idle"); });

        const facing = getFacingDirection();
        const offset = facing.scale(16);
        state.spawnSlashParticles(player.pos.add(offset), facing);

        const hit = k.add([k.rect(16, 12), k.area(), k.pos(player.pos.add(offset)), k.opacity(1), k.lifespan(0.12), "punch"]);
        const hitTargets = new Set();
        hit.onCollide("foe", (foe) => {
            if (hitTargets.has(foe)) return;
            hitTargets.add(foe);
            registerCombo();
            damageEnemy(foe, BASE_DAMAGE);
        });
    }

    function performChargedAttack() {
        if (!player.canPunch) return;
        const { CHARGED_COOLDOWN, CHARGED_DAMAGE } = state.constants;
        player.canPunch = false;
        player.cooldownTimer = CHARGED_COOLDOWN;
        player.play("punch");
        k.play("sfx_punch", { volume: 0.8 });
        k.wait(0.28, () => { if (!state.gameOver && player.exists()) player.play("idle"); });
        
        const facing = getFacingDirection();
        const offset = facing.scale(22);
        state.spawnSlashParticles(player.pos.add(offset), facing, k.rgb(255, 210, 140));
        state.spawnImpactBurst(player.pos.add(offset.scale(0.4)), k.rgb(255, 190, 140));

        const hit = k.add([k.rect(24, 16), k.area(), k.pos(player.pos.add(offset)), k.opacity(1), k.lifespan(0.16), "chargedPunch"]);
        const hitTargets = new Set();
        hit.onCollide("foe", (foe) => {
            if (hitTargets.has(foe)) return;
            hitTargets.add(foe);
            registerCombo();
            damageEnemy(foe, CHARGED_DAMAGE);
            state.spawnParrySparks(foe.pos);
        });
    }

    function triggerSpecialAttack() {
        if (state.gameOver || state.menuActive || !player.specialReady) return;
        k.play("sfx_ui_nav");
        const { SPECIAL_COOLDOWN, SPECIAL_DAMAGE, SPECIAL_KNOCKBACK } = state.constants;
        player.specialReady = false;
        player.specialCooldownTimer = SPECIAL_COOLDOWN;
        player.play("charged");
        k.wait(0.3, () => { if (!state.gameOver && player.exists()) player.play("idle"); });

        const facing = getFacingDirection();
        const spawnOffset = facing.scale(22);
        state.spawnSlashParticles(player.pos.add(spawnOffset), facing, k.rgb(255, 210, 140));
        k.play("sfx_punch", { volume: 0.75 });
        const hit = k.add([k.rect(26, 18), k.area(), k.pos(player.pos.add(spawnOffset)), k.opacity(1), k.lifespan(0.18), "specialPunch"]);
        k.shake(6);
        const hitTargets = new Set();
        hit.onCollide("foe", (foe) => {
            if (hitTargets.has(foe)) return;
            hitTargets.add(foe);
            registerCombo();
            damageEnemy(foe, SPECIAL_DAMAGE);
            foe.pos = foe.pos.add(facing.scale(SPECIAL_KNOCKBACK));
        });
    }

    // --- INPUT & UPDATES ---
    k.onKeyPress("enter", () => {
        if (state.tutorialActive) exitTutorial();
        else if (state.menuActive) beginFight();
        else if (state.gameOver) restartGame();
    });
    k.onKeyPress("m", () => {
        if (state.gameOver) returnToMenu();
    });
    k.onKeyPress("f", () => { if (!state.menuActive) triggerSpecialAttack(); });
    k.onKeyDown("space", () => {
        if (state.menuActive || state.gameOver || !player.canPunch || state.isCharging) return;
        state.isCharging = true;
        state.chargeStart = k.time();
        state.chargeReady = false;
        player.play("charged");
        state.chargeAura = k.add([ k.pos(player.pos), k.circle(28), k.color(120, 190, 255), k.opacity(0.18) ]);
    });
    k.onKeyRelease("space", () => {
        if (!state.isCharging) { cleanupChargeAura(); return; }
        const held = k.time() - state.chargeStart;
        cleanupChargeAura();
        if (state.menuActive || state.gameOver || !player.canPunch) return;
        if (held >= state.constants.CHARGE_TIME) performChargedAttack();
        else performLightAttack();
    });

    k.onUpdate(() => {
        if (state.menuActive || state.gameOver) return;
        updateCurrentEnemy();

        if (state.powerUpTimer > 0) {
            state.powerUpTimer -= k.dt();
            if (state.powerUpTimer <= 0) {
                state.activePowerUp = null;
                powerUpAura.opacity = 0;
            }
        }
        powerUpAura.pos = player.pos;
        
        if (!player.canPunch) {
            player.cooldownTimer -= k.dt();
            if (player.cooldownTimer <= 0) player.canPunch = true;
        }
        if (!player.specialReady) {
            player.specialCooldownTimer -= k.dt();
            if (player.specialCooldownTimer <= 0) player.specialReady = true;
        }
        if (state.comboTimer > 0) {
            state.comboTimer -= k.dt();
            if (state.comboTimer <= 0) resetCombo();
        }
        if (state.chargeAura) {
            state.chargeAura.pos = player.pos;
            const elapsed = k.time() - state.chargeStart;
            if (!state.chargeReady && elapsed >= state.constants.CHARGE_TIME) {
                state.chargeReady = true;
                k.play("sfx_charge", { volume: 0.5 });
                state.spawnParrySparks(player.pos.add(getFacingDirection().scale(6)));
            }
        }
        ui.updateAllLabels();
        ui.updateHealthBars();
        ui.comboLabel.hidden = state.comboCount <= 0;
        if (!ui.comboLabel.hidden) ui.comboLabel.text = `combo x${state.comboCount}`;
    });
    
    // --- INITIALIZATION ---
    if (state.tutorialActive) ui.showTutorial();
    else ui.showMenu();
    resetPlayerState();
    ui.updateAllLabels();
    ui.updateHealthBars();
  });
}
